import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import {
  useDepartmentStore,
  usePremisesStore,
  useRegionStore,
} from "../../../store/store";
import { useLocationStore } from "../../../store/masterDataStore";
import { LocationType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm = {
  regionId: "",
  premiseId: "",
  department: "",
  floorNumber: "",
  room: "",
  zone: "",
  locationCode: "",
  description: "",
};

const LocationMaster: React.FC = () => {
  const [rowData, setRowData] = useState<any[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const { allPremises, fetchPremises } = usePremisesStore();
  const { departments, fetchAllDepartments } = useDepartmentStore();
  const { regions, fetchRegions } = useRegionStore();
  const {
    createLocation,
    updateLocation,
    deleteLocation,
    fetchAllLocations,
    allLocations,
  } = useLocationStore();
  const [editId, setEditId] = useState<string | null>(null);

  const floorsForSelectedPremise = useMemo(() => {
    if (!formData.premiseId) return [];
    const premise = allPremises.find((p) => p._id === formData.premiseId);
    if (!premise) return [];

    const floorCount = premise?.floor || 0;
    const floors = [];
    for (let i = 0; i < floorCount; i++) {
      floors.push(i);
    }
    return floors;
  }, [allPremises, formData.premiseId]);

  useEffect(() => {
    fetchPremises();
    fetchRegions();
    fetchAllDepartments();
    fetchAllLocations();
  }, []);

  useEffect(() => {
    setRowData(allLocations);
  }, [allLocations]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === "regionId") {
      setFormData((prev) => ({
        ...prev,
        premiseId: "",
        department: "",
        floorNumber: "",
      }));
    }
    if (e.target.name === "premiseId") {
      setFormData((prev) => ({ ...prev, department: "", floorNumber: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      if (isEditMode) {
        response = await updateLocation(formData, editId || "");
      } else {
        response = await createLocation(formData);
      }
      if (response) {
        setModalOpen(false);
        setFormData(emptyForm);
        setIsEditMode(false);
        setEditId(null);
      }
    } catch (error) {
      console.error("Error creating location:", error);
    }
  };

  const handleEdit = (data: LocationType) => {
    setEditId(data._id || null);
    setIsEditMode(true);
    setFormData({
      regionId: data.regionId,
      premiseId: data.premiseId,
      department: data.department,
      floorNumber: data.floorNumber,
      room: data.room,
      zone: data.zone || "",
      locationCode: data.locationCode || "",
      description: data.description || "",
    });
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this location?"
    );
    if (confirmed) {
      await deleteLocation(id);
    }
  };

  const columnDefs = useMemo(() => {
    const regionMap = new Map(regions?.map((r) => [r._id, r.regionName]));
    const premiseMap = new Map(
      allPremises?.map((p) => [p._id, p.premisesName])
    );
    const departmentMap = new Map(
      departments?.map((d) => [d._id, d.departmentName])
    );

    return [
      {
        field: "room",
        headerName: "Room",
        minWidth: 90,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "regionId",
        headerName: "Region",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          regionMap.get(params.data.regionId) || params.data.regionId || "N/A",
      },
      {
        field: "premiseId",
        headerName: "Premise",
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          premiseMap.get(params.data.premiseId) ||
          params.data.premiseId ||
          "N/A",
      },
      {
        field: "department",
        headerName: "Department",
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          departmentMap.get(params.data.department) ||
          params.data.department ||
          "N/A",
      },
      {
        field: "floorNumber",
        headerName: "Floor Number",
        minWidth: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "zone",
        headerName: "Zone / Section",
        minWidth: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "locationCode",
        headerName: "Location Code",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 90,
        maxWidth: 110,
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => (
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
      },
    ];
  }, [regions, allPremises, departments, images.editIcon]);

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      filter: false,
      tooltipComponentParams: { color: "#ececec" },
    }),
    []
  );

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Location Master</h2>
          <button
            className="flex items-center gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-teal-600 font-semibold"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Location Info
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData.length ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg border-0">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={gridTheme}
                  headerHeight={65}
                  rowHeight={50}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <div className="flex justify-center items-center h-64">
                <p className="text-gray-500 text-lg">
                  No location data added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg max-h-[80vh] flex flex-col relative"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Location Details"
              : "Create New Location Details"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="locationForm"
            className="flex flex-col gap-6 pb-24"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="regionId"
                  className="text-sm font-medium text-gray-700"
                >
                  Region (State/Area) <span className="text-red-500">*</span>
                </label>
                <select
                  id="regionId"
                  name="regionId"
                  value={formData.regionId}
                  onChange={handleChangeSelect}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="" disabled>
                    Select Region
                  </option>
                  {regions?.map((region: any) => (
                    <option key={region._id} value={region._id}>
                      {region.regionName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="premiseId"
                  className="text-sm font-medium text-gray-700"
                >
                  Premise (Hospital) <span className="text-red-500">*</span>
                </label>
                <select
                  id="premiseId"
                  name="premiseId"
                  value={formData.premiseId}
                  onChange={handleChangeSelect}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="" disabled>
                    Select Premise
                  </option>
                  {allPremises
                    ?.filter(
                      (premise: any) => premise.region === formData.regionId
                    )
                    .map((premise: any) => (
                      <option key={premise._id} value={premise._id}>
                        {premise.premisesName}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="department"
                  className="text-sm font-medium text-gray-700"
                >
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChangeSelect}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="" disabled>
                    Select Department
                  </option>
                  {departments.map((department: any) => (
                    <option key={department._id} value={department._id}>
                      {department.departmentName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="floorNumber"
                  className="text-sm font-medium text-gray-700"
                >
                  Floor Number <span className="text-red-500">*</span>
                </label>
                <select
                  id="floorNumber"
                  name="floorNumber"
                  value={formData.floorNumber}
                  onChange={handleChangeSelect}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="" disabled>
                    Select Floor Number
                  </option>
                  {floorsForSelectedPremise?.map((floor: any) => (
                    <option key={floor} value={floor}>
                      {floor + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="room"
                  className="text-sm font-medium text-gray-700"
                >
                  Room <span className="text-red-500">*</span>
                </label>
                <input
                  id="room"
                  name="room"
                  type="text"
                  value={formData.room}
                  onChange={handleChange}
                  required
                  placeholder="Room number or name"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="zone"
                  className="text-sm font-medium text-gray-700"
                >
                  Zone / Section
                </label>
                <input
                  id="zone"
                  name="zone"
                  type="text"
                  value={formData.zone}
                  onChange={handleChange}
                  placeholder="Zone or section"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="locationCode"
                className="text-sm font-medium text-gray-700"
              >
                Location Code / ID
              </label>
              <input
                id="locationCode"
                name="locationCode"
                type="text"
                value={formData.locationCode}
                onChange={handleChange}
                placeholder="Code or identifier"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="description"
                className="text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Additional notes"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>
          </form>
        </div>

        <div className="flex justify-end gap-4 p-4 bg-[#5A7192] rounded-b-lg sticky bottom-0">
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            className="px-6 py-2 rounded-full bg-[#8497B0] text-white hover:bg-teal-600 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="locationForm"
            className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Create"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default LocationMaster;
